import { Routes } from "@angular/router";

import { LoginComponent } from "./components/pages/login/login.component";
import { DashboardComponent } from "./components/pages/dashboard/dashboard.component";
import { KpiComponent } from "./components/pages/kpi/kpi.component";
import { MapComponent } from "./components/pages/map/map.component";
import { ListComponent } from "./components/pages/list/list.component";
import { AnalyticsComponent } from "./components/pages/analytics/analytics.component";
import { IncidentComponent } from "./components/pages/incident/incident.component";
import { ManagerComponent } from "./components/pages/manager/manager.component";
import { tabRouteData, LANDING_SEGMENT } from "./tabs";
import { authGuard } from "./guards/auth.guard";
import { roleGuard } from "./guards/role.guard";
import { notFoundGuard } from "./guards/not-found.guard";

/**
 * Clean-URL route tree for the MPA migration (§3.1 of the plan).
 *
 * - `/` is the login page with `pathMatch: "full"` so SSO callbacks
 *   (`/?id=&jwt=`) land there.
 * - The authenticated shell is a second empty-path route (prefix match) whose
 *   children are the feature pages, rendered into the DashboardComponent shell's
 *   `<router-outlet>`. `authGuard` protects the whole subtree.
 * - Deep-link `:id` / `:graphSlug` routes point at the existing page component;
 *   the component reads the param and drives its own fetch/selection (wired in
 *   Phase 4). Manager sub-routes are grouped under a componentless, role-guarded
 *   route so the guard is declared once.
 * - The two fleet tabs carry TWO record siblings each, `site/:site_code` and
 *   `tracker/:tracker_id`, declared flat rather than nested: they are mutually
 *   exclusive views of the same page, so a device opened from a site view leaves
 *   the site (`/map/site/88` -> `/map/tracker/AB12`) and Back returns to it. There
 *   is deliberately no `map/site/:site_code/tracker/:tracker_id`.
 * - What a top-bar segment means beyond its path — which role it needs, whether
 *   its component survives the hop to its record sibling — comes from TAB_SEGMENTS
 *   (src/app/tabs.ts) through `tabRouteData`, so the guard and the reuse strategy
 *   read one declaration instead of two that have to agree.
 * - A parent segment with no view of its own redirects to its default child
 *   (`/manager` to `/manager/users`, `/manager/references` to `.../sites`) so
 *   each section has exactly one URL instead of two rendering the same page.
 */
export const routes: Routes = [
  { path: "", component: LoginComponent, pathMatch: "full" },
  {
    path: "",
    component: DashboardComponent,
    canActivate: [authGuard],
    children: [
      { path: "kpi", component: KpiComponent },

      { path: "map", component: MapComponent, data: tabRouteData("map") },
      {
        path: "map/site/:site_code",
        component: MapComponent,
        data: tabRouteData("map")
      },
      {
        path: "map/tracker/:tracker_id",
        component: MapComponent,
        data: tabRouteData("map")
      },

      { path: "list", component: ListComponent, data: tabRouteData("list") },
      {
        path: "list/site/:site_code",
        component: ListComponent,
        data: tabRouteData("list")
      },
      {
        path: "list/tracker/:tracker_id",
        component: ListComponent,
        data: tabRouteData("list")
      },

      {
        path: "analytics",
        canActivate: [roleGuard],
        data: tabRouteData("analytics"),
        children: [
          { path: "", component: AnalyticsComponent },
          { path: ":graphSlug", component: AnalyticsComponent }
        ]
      },

      { path: "incident", component: IncidentComponent },
      { path: "incident/:incident_id", component: IncidentComponent },

      {
        path: "manager",
        canActivate: [roleGuard],
        data: tabRouteData("manager"),
        children: [
          { path: "", redirectTo: "users", pathMatch: "full" },
          { path: "users", component: ManagerComponent },
          { path: "users/:user_id", component: ManagerComponent },
          { path: "groups", component: ManagerComponent },
          { path: "groups/:group_id", component: ManagerComponent },
          {
            path: "references",
            redirectTo: "references/sites",
            pathMatch: "full"
          },
          { path: "references/sites", component: ManagerComponent },
          { path: "references/sites/:site_id", component: ManagerComponent },
          { path: "references/packagings", component: ManagerComponent },
          {
            path: "references/packagings/:packaging_id",
            component: ManagerComponent
          },
          { path: "references/routes", component: ManagerComponent },
          { path: "references/routes/:route_id", component: ManagerComponent },
          { path: "references/mac-addresses", component: ManagerComponent },
          {
            path: "references/mac-addresses/:mac_address_id",
            component: ManagerComponent
          },
          { path: "references/hybrid-matching", component: ManagerComponent },
          { path: "trackers", component: ManagerComponent },
          { path: "trackers/:tracker_id", component: ManagerComponent },
          { path: "tokens", component: ManagerComponent }
        ]
      },

      { path: "", redirectTo: LANDING_SEGMENT, pathMatch: "full" }
    ]
  },
  // Unknown URL: notFoundGuard always redirects (notice + the landing tab when
  // signed in, the login page otherwise), so this route never renders.
  { path: "**", canActivate: [notFoundGuard], children: [] }
];
